import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';

import {
  Box,
  BoxSizeOptions,
  PaddingSize,
} from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import { BodyText, Stack } from '../../../ui';
import ServiceItem from '../ServiceItem';
import { useIsMobile } from '../../../../hooks';
import { ServiceSection } from './types';
import type { RootState } from '../../../../store/models';
import { ProfileServiceGroupsState } from '../../../../store/ProfileServiceGroups';
import { ProviderService } from '../../../../api/Providers/Services';
import { PublicProvider } from '../../../../api/Providers';
import { PreviouslyBookedServicesContext } from '../ServicesContext';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';
import { HighlightedServicesStackContext } from './HighlightedServicesStackProvider';

const MIN_POPULAR_SERVICES = 2;
const MIN_LAST_SERVICES = 2;

type ServiceStackItemProps = {
  provider: PublicProvider | ClientProviderProfile;
  showPopularServices: boolean;
  itemPaddingX: BoxSizeOptions;
  itemColor: string;
  index: number;
  service: ProviderService;
  groups?: ProfileServiceGroupsState['groupsById'];
};

const ServiceStackItem: React.FC<ServiceStackItemProps> = ({
  provider,
  showPopularServices,
  itemPaddingX,
  itemColor,
  index,
  service,
  groups,
}) => {
  const extra = showPopularServices ? 'popularswiper' : 'last_services_swiper';

  return (
    <Box
      key={service.id}
      paddingX={itemPaddingX}
      borderWidth={1}
      borderStyle="solid"
      borderColor={BrandColor.LightGrey}
    >
      <ServiceItem
        backgroundColor={itemColor}
        key={service.id}
        service={{ ...service, isTopService: false } as any}
        providerId={provider.id}
        providerName={provider.name}
        index={index}
        group={groups?.[service.servicegroup]}
        seeReviewsProps={{
          provider,
          service,
          salon: {},
          extra,
        }}
        additionalTracking={{
          extra,
        }}
        allowOnlineBooking={
          provider.is_effectively_online_bookable && !provider.is_disabled
        }
        useShortDescription
        hideTags
      />
    </Box>
  );
};

export type HighlightedServicesStackProps = {
  provider: PublicProvider | ClientProviderProfile;
  serviceGroups: ReadonlyArray<ServiceSection>;
  highlightedServiceId?: number;
  isLoggedIn?: boolean;
  isInSortedServicesTest?: boolean;
};

export const HighlightedServicesStack: React.FC<HighlightedServicesStackProps> = ({
  provider,
  serviceGroups,
  isLoggedIn,
  highlightedServiceId,
  isInSortedServicesTest,
}) => {
  const inMobileLayout = useIsMobile();
  const [showPopularServices, setShowPopularServices] = useState<boolean>(true);
  const {
    topServicesData: popularServices, highlightedService, setHighlightedService,
  } = useContext(HighlightedServicesStackContext);
  const groups = useSelector<RootState, ProfileServiceGroupsState['groupsById']>(
    state => state.profileServiceGroups?.[provider.id]?.groupsById,
  );

  const lastBookedServices = useContext(PreviouslyBookedServicesContext);

  useEffect(() => {
    setShowPopularServices(!isLoggedIn || lastBookedServices?.length < MIN_LAST_SERVICES);
  }, [
    lastBookedServices,
    isLoggedIn,
  ]);

  /** check if highlighted service exists */
  useEffect(() => {
    // don't do work if not in test (highlightedServiceId is undefined) or already found
    if (!highlightedService && highlightedServiceId && serviceGroups) {
      // service groups are actually service sections generated around line 350 of
      // `ServiceListContainer`
      (serviceGroups || []).forEach((serviceSection: ServiceSection) => {
        serviceSection.data.forEach((service: ProviderService) => {
          if (service.id === highlightedServiceId) {
            setHighlightedService({ ...service, isHighlight: true });
          }
        });
      });
    }
  }, [
    highlightedServiceId,
    serviceGroups,
    highlightedService,
    setHighlightedService,
  ]);

  const {
    itemPaddingX, itemColor, wrapperColor,
  } = useMemo(() => {
    if (inMobileLayout) {
      return {
        itemPaddingX: 8 as PaddingSize,
        itemColor: BrandColor.WhiteWash,
        wrapperColor: BrandColor.BoneLight,
      };
    }
    return {
      itemPaddingX: 0 as PaddingSize,
      itemColor: BrandColor.BoneLight,
    };
  }, [inMobileLayout]);

  const filteredServices: Array<ProviderService> = React.useMemo(() => {
    const filterServices = servicesList => (
      servicesList?.filter(service => service.id !== highlightedServiceId)
    );
    let serviceList: Array<ProviderService> = [];
    if (showPopularServices) {
      serviceList = filterServices(popularServices);
    } else {
      serviceList = filterServices(lastBookedServices);
    }
    return serviceList || [];
  }, [
    popularServices,
    showPopularServices,
    lastBookedServices,
    highlightedServiceId,
  ]);

  const {
    titleText,
    testID,
    descriptionText,
  } = useMemo((): { titleText: string; testID: string; descriptionText: string } => {
    let title: string = 'Top Services';
    let id: string = 'highlighted-service-stack';
    let description: string = `${provider.name}'s most commonly booked services`;

    if (!highlightedService && lastBookedServices?.length >= MIN_LAST_SERVICES) {
      // if we're displaying last booked services
      title = 'Last Booked';
      id = 'last-service-stack';

      // or if we're displaying popular services instead
      if (showPopularServices) {
        description = '';
        id = 'popular-service-stack';
      }
    }

    return {
      titleText: title, testID: id, descriptionText: description,
    };
  }, [
    highlightedService,
    lastBookedServices,
    provider.name,
    showPopularServices,
  ]);

  if (
    (isInSortedServicesTest && highlightedServiceId)
    || (!highlightedService && filteredServices?.length === 0)
    || (
      !highlightedService
      && showPopularServices
      && filteredServices?.length < MIN_POPULAR_SERVICES
    )
  ) {
    return null;
  }

  return (
    <Stack
      space={4}
      color={wrapperColor}
      paddingBottom={8}
      testID="stack_outer_container_1252"
    >
      <Box paddingTop={16} paddingLeft={8}>
        <BodyText weight="bold" textAlign="left">
          {titleText}
        </BodyText>
        {descriptionText && (
          <Box paddingY={8}>
            <BodyText
              weight="thin"
              textAlign="left"
              appearance="small"
              color={BrandColor.Metal}
            >
              {descriptionText}
            </BodyText>
          </Box>
        )}
      </Box>
      <Box
        flex={1}
        testID={testID}
        style={{
          minHeight: 200, gap: '8px', marginBottom: '10px',
        } as any}
      >
        {!!highlightedService && (
          <ServiceStackItem
            key={highlightedService.id}
            service={highlightedService}
            index={0}
            groups={groups}
            provider={provider}
            showPopularServices={showPopularServices}
            itemPaddingX={itemPaddingX}
            itemColor={itemColor}
          />
        )}
        {filteredServices.map((service, index) => (
          <ServiceStackItem
            key={service.id}
            service={service}
            index={index + 1}
            groups={groups}
            provider={provider}
            showPopularServices={showPopularServices}
            itemPaddingX={itemPaddingX}
            itemColor={itemColor}
          />
        ))}
      </Box>
    </Stack>
  );
};
